import { LockOutlined } from "@mui/icons-material";
import { Alert, Box, Button, Paper, Typography } from "@mui/material";
import { useQueryClient } from "react-query";
import { usePermission } from "src/helpers/Auth";
import { useGetUserDetails } from "src/helpers/UserDetails";
import { useAddResourceUserShare } from "src/api/shares";

/**
 * Rendered when the dataset viewer's content read is denied (403).
 *
 * For admins this is the break-glass affordance: admins hold per-object
 * "manage" on every dataset (administrative bypass) but deliberately NOT
 * content read (separation of duties), so the sanctioned way for an admin
 * to open a private dataset is to grant themselves access — an explicit,
 * audited ResourcePermission row that the owner can see and revoke in the
 * dataset's sharing settings. This component makes that one click instead
 * of an API call.
 *
 * Everyone else gets a plain access-denied message (previously the viewer
 * rendered an empty shell on 403).
 */
const DatasetAccessDenied = ({ datasetId }: { datasetId: number }) => {
  const { canIt: isAdmin } = usePermission("admin");
  const currentUser = useGetUserDetails();
  const selfGrant = useAddResourceUserShare("dataset");
  const queryClient = useQueryClient();

  const handleSelfGrant = () => {
    if (!currentUser?.id) return;
    selfGrant.mutate(
      {
        resourceId: datasetId,
        data: { user_id: currentUser.id, permission_level: "read" },
      },
      {
        onSuccess: () => {
          // The share hook's own onSuccess invalidates the dataset detail,
          // which re-renders the viewer in place. But the viewer's sibling
          // queries (source records, fields, filter schema, ...) also fired
          // before the grant and cached their own 403s — react-query won't
          // retry an errored query on re-render, so they'd keep showing
          // stale access errors. Every currently-errored query errored
          // because access was missing a moment ago: refetch them all.
          queryClient.invalidateQueries({
            predicate: (query) => query.state.status === "error",
          });
        },
      },
    );
  };

  return (
    <Box
      data-testid="ds-access-denied"
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "60vh",
        px: 2,
      }}
    >
      <Paper
        variant="outlined"
        sx={{ maxWidth: 480, p: 4, textAlign: "center" }}
      >
        <LockOutlined sx={{ fontSize: 40, color: "text.secondary", mb: 1 }} />
        <Typography variant="h6" gutterBottom>
          This dataset is private
        </Typography>
        {isAdmin ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You don&apos;t have access to this dataset&apos;s contents. As an
              administrator you can grant yourself view access. The grant is
              recorded in the audit log and is visible to the dataset&apos;s
              owner in its sharing settings, where it can be revoked.
            </Typography>
            {selfGrant.isError && (
              <Alert severity="error" sx={{ mb: 2, textAlign: "left" }}>
                Granting access failed. Please try again.
              </Alert>
            )}
            <Button
              variant="contained"
              disableElevation
              startIcon={<LockOutlined />}
              onClick={handleSelfGrant}
              disabled={selfGrant.isLoading || !currentUser?.id}
              data-testid="ds-btn-self-grant"
              data-attr={`datasets:btn:admin-self-grant-${datasetId}`}
            >
              {selfGrant.isLoading ? "Granting…" : "Grant myself view access"}
            </Button>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            You don&apos;t have access to this dataset. Ask the dataset&apos;s
            owner to share it with you.
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default DatasetAccessDenied;
